import { useState } from "react";
import { Link, useLocation, useParams } from "wouter";
import { CheckCircle2, Circle, Copy, Eye, EyeOff, Send, Sparkles } from "lucide-react";
import { AppLayout } from "@/components/layout/AppLayout";
import { Badge, Card, PrimaryButton, SectionTitle } from "@/components/Card";
import { StandardTabsHeader } from "@/components/StandardTabsHeader";
import { useDemoStore } from "@/store/useDemoStore";
import { useElementsForStandard, useInstrumentsForStandard, useRulesForStandard, useStandard } from "@/store/hooks";
import type { Standard } from "@/data";

function today() {
  return new Date().toISOString().slice(0, 10);
}

const checks = [
  { id: 1, label: "Datos generales del estándar completos", done: true },
  { id: 2, label: "Elementos E5637 y E5638 con criterios validados", done: true },
  { id: 3, label: "Instrumentos asociados (4) y banco de reactivos vinculado", done: true },
  { id: 4, label: "Reglas de dictamen (7 familias) revisadas", done: true },
  { id: 5, label: "Equipo evaluador y dictaminador asignado", done: true },
  { id: 6, label: "Aprobación del comité académico CONOCER", done: true },
];

export default function PublicationReview() {
  const params = useParams<{ codigo: string }>();
  const codigo = params.codigo ?? "EC1781";
  const [, setLocation] = useLocation();
  const publishStandardSeed = useDemoStore((s) => s.publishStandard);
  const published = useDemoStore((s) => s.mutations.standardPublished);
  const [submitting, setSubmitting] = useState(false);
  const standard = useStandard(codigo);

  const standardsMap = useDemoStore((s) => s.standards);
  const archivedMap = useDemoStore((s) => s.archivedStandards);
  const addStandard = useDemoStore((s) => s.addStandard);
  const updateStandard = useDemoStore((s) => s.updateStandard);
  const setElementsForStandard = useDemoStore((s) => s.setElementsForStandard);
  const setRulesForStandard = useDemoStore((s) => s.setRulesForStandard);
  const setInstrumentsForStandard = useDemoStore((s) => s.setInstrumentsForStandard);
  const logEvent = useDemoStore((s) => s.logEvent);
  const activeRole = useDemoStore((s) => s.activeRole);
  const elems = useElementsForStandard(codigo);
  const rules = useRulesForStandard(codigo);
  const instruments = useInstrumentsForStandard(codigo);

  function publishQuick() {
    updateStandard(codigo, { estado: "publicado", ultimaActualizacion: today() });
    logEvent({ actor: "Administrador académico", rol: activeRole, accion: "publicar", entidad: "standard", entidadId: codigo });
  }

  function unpublish() {
    if (!window.confirm(`Despublicar ${codigo}? Volverá al estado "en revisión".`)) return;
    updateStandard(codigo, { estado: "en-revision", ultimaActualizacion: today() });
    logEvent({ actor: "Administrador académico", rol: activeRole, accion: "despublicar", entidad: "standard", entidadId: codigo });
  }

  function sendToReview() {
    updateStandard(codigo, { estado: "en-revision", ultimaActualizacion: today() });
    logEvent({ actor: "Administrador académico", rol: activeRole, accion: "enviar-revision", entidad: "standard", entidadId: codigo });
    window.alert(`${codigo} v1.2 enviado al comité para revisión.`);
  }

  if (!standard) {
    return (
      <AppLayout breadcrumb={["Estándares", codigo, "Publicación"]}>
        <Card>
          <SectionTitle>Estándar no encontrado</SectionTitle>
          <Link href="/admin/estandares" data-testid="link-back-list">
            <PrimaryButton variant="outline">Volver al catálogo</PrimaryButton>
          </Link>
        </Card>
      </AppLayout>
    );
  }

  return (
    <AppLayout breadcrumb={["Estándares", codigo, "Publicación"]}>
      <StandardTabsHeader
        standard={standard}
        active="publicacion"
        rightAction={
          <>
            {standard.estado === "publicado" ? (
              <PrimaryButton variant="outline" onClick={unpublish} testId="button-unpublish"><EyeOff size={13} /> Despublicar</PrimaryButton>
            ) : (
              <PrimaryButton onClick={publishQuick} testId="button-publish-quick"><Eye size={13} /> Publicar</PrimaryButton>
            )}
          </>
        }
      />

      <div style={{ marginBottom: 14, fontSize: 13, color: "var(--cc-gray-600)" }}>
        Versión 1.2 · {published ? "Publicada" : "Lista para publicar"}
      </div>

      <div style={{ display: "grid", gridTemplateColumns: "2fr 1fr", gap: 16 }}>
        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Card>
            <SectionTitle>Lista de verificación previa</SectionTitle>
            <div style={{ display: "flex", flexDirection: "column", gap: 8 }}>
              {checks.map((c) => (
                <div key={c.id} style={{ display: "flex", alignItems: "center", gap: 10, padding: 10, border: "1px solid var(--cc-gray-100)", borderRadius: 8 }} data-testid={`check-${c.id}`}>
                  {c.done ? <CheckCircle2 size={18} color="var(--cc-green)" /> : <Circle size={18} color="var(--cc-gray-400)" />}
                  <div style={{ fontSize: 13, color: c.done ? "var(--cc-gray-700)" : "var(--cc-gray-500)" }}>{c.label}</div>
                  <div style={{ marginLeft: "auto" }}>{c.done && <Badge tone="green">OK</Badge>}</div>
                </div>
              ))}
            </div>
          </Card>

          <Card>
            <div style={{ display: "flex", alignItems: "center", justifyContent: "space-between", gap: 12, flexWrap: "wrap" }}>
              <div>
                <div style={{ fontSize: 13, fontWeight: 700, color: "var(--cc-navy)" }}>
                  {published ? "Estándar publicado" : "¿Listo para publicar?"}
                </div>
                <div style={{ fontSize: 12, color: "var(--cc-gray-600)", marginTop: 2 }}>
                  {published
                    ? `${codigo} v1.2 ya está disponible para inscripciones y evaluaciones.`
                    : `Envía ${codigo} v1.2 a revisión del comité o publícalo directamente si ya tienes los visados.`}
                </div>
              </div>
              <div style={{ display: "flex", gap: 8, flexWrap: "wrap" }}>
                <PrimaryButton variant="outline" testId="button-export-pdf">Exportar borrador PDF</PrimaryButton>
                {!published && (
                  <PrimaryButton variant="outline" onClick={sendToReview} testId="button-send-review">
                    Enviar a revisión
                  </PrimaryButton>
                )}
                {!published && (
                  <PrimaryButton
                    variant="dark"
                    onClick={() => {
                      setSubmitting(true);
                      setTimeout(() => { publishStandardSeed(); setSubmitting(false); }, 700);
                    }}
                    testId="button-publish"
                  >
                    {submitting ? "Publicando..." : `Publicar ${codigo} v1.2`} <Send size={13} />
                  </PrimaryButton>
                )}
              </div>
            </div>
          </Card>
        </div>

        <div style={{ display: "flex", flexDirection: "column", gap: 14 }}>
          <Card style={{ background: "linear-gradient(180deg, #ecfeff 0%, #f0fdfa 100%)" }}>
            <div style={{ display: "flex", alignItems: "center", gap: 8, marginBottom: 6 }}>
              <Sparkles size={15} color="var(--cc-teal)" />
              <span style={{ fontSize: 11, fontWeight: 700, color: "var(--cc-teal)", letterSpacing: "0.06em", textTransform: "uppercase" }}>Validación IA</span>
            </div>
            <div style={{ fontSize: 13, color: "var(--cc-gray-700)", lineHeight: 1.6 }}>
              El agente IA revisó la consistencia interna del estándar de competencia <strong>{codigo}</strong>: contrastó
              criterios, evidencias e instrumentos contra los elementos publicados y la ficha CONOCER del propio estándar.
              <strong> No se detectaron contradicciones</strong> entre criterios e instrumentos. Cobertura semántica del
              estándar: <strong>97.1%</strong>.
            </div>
          </Card>
        </div>
      </div>
    </AppLayout>
  );
}
